import { ResourceFieldRenderer } from '@/components/crud/ResourceFieldRenderer'
import {
  DetailSheet,
  type DetailSheetTab
} from '@/components/primitives/DetailSheet'
import { Button } from '@/components/ui/button'
import { splitEssential } from '@/lib/field-categories'
import type {
  ResourceField,
  ResourceRow,
  ResourceSchema
} from '@/lib/resources'
import { ChevronDown, ChevronRight, Pencil, Trash2 } from 'lucide-react'
import { type ReactNode, useState } from 'react'

export interface ExtraDetailTab {
  id: string
  label: string
  render: (row: ResourceRow) => ReactNode
  badge?: ReactNode
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  schema: ResourceSchema
  row: ResourceRow | null
  onEdit?: () => void
  onDelete?: () => void
  extraTabs?: ExtraDetailTab[]
  rowActions?: ReactNode
}

function FieldRow({
  field,
  row,
  schema
}: { field: ResourceField; row: ResourceRow; schema: ResourceSchema }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-baseline">
      <dt className="text-xs font-medium text-muted-foreground truncate">
        {field.label}
      </dt>
      <dd className="break-words text-sm">
        <ResourceFieldRenderer
          field={field}
          value={row[field.name]}
          resourceKey={schema.key}
          rowId={row.id as string | number}
        />
      </dd>
    </div>
  )
}

function FieldList({
  fields,
  row,
  schema
}: { fields: ResourceField[]; row: ResourceRow; schema: ResourceSchema }) {
  return (
    <div className="space-y-3">
      {fields.map((field) => (
        <FieldRow key={field.name} field={field} row={row} schema={schema} />
      ))}
    </div>
  )
}

export function ResourceDetailSheet({
  open,
  onOpenChange,
  schema,
  row,
  onEdit,
  onDelete,
  extraTabs = [],
  rowActions
}: Props) {
  if (!row) return null

  const { core, extra } = splitEssential(schema.detail_fields)
  const [showExtra, setShowExtra] = useState(false)

  const detailTab: DetailSheetTab = {
    id: 'detail',
    label: 'Details',
    content: (
      <div className="space-y-4">
        {core.length > 0 && (
          <FieldList fields={core} row={row} schema={schema} />
        )}

        {extra.length > 0 && (
          <div className="border-t pt-4">
            <button
              type="button"
              onClick={() => setShowExtra(!showExtra)}
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {showExtra ? (
                <ChevronDown className="size-3" />
              ) : (
                <ChevronRight className="size-3" />
              )}
              {showExtra
                ? 'Hide extra fields'
                : `Show ${extra.length} more field${extra.length > 1 ? 's' : ''}`}
            </button>
            {showExtra && (
              <div className="mt-4">
                <FieldList fields={extra} row={row} schema={schema} />
              </div>
            )}
          </div>
        )}

        {core.length === 0 && extra.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            No fields available.
          </p>
        )}
      </div>
    )
  }

  const tabs: DetailSheetTab[] = [
    detailTab,
    ...extraTabs.map((t) => ({
      id: t.id,
      label: t.label,
      content: t.render(row),
      badge: t.badge
    }))
  ]

  const titleField = ['name', 'title', 'username', 'email', 'label'].find(
    (f) => row[f] !== undefined && row[f] !== null && row[f] !== ''
  )
  const title = titleField
    ? String(row[titleField])
    : `${schema.singular} #${String(row.id ?? '')}`

  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={schema.singular}
      whatIsThis={schema.description}
      tabs={tabs}
      footer={
        <div className="flex items-center gap-2">
          {rowActions}
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="size-3.5" /> Edit
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="size-3.5" /> Delete
            </Button>
          )}
        </div>
      }
    />
  )
}
